import {Hierarchy} from '@/library/hierarchy';
import Loader from '@/library/intranet/loader';
import {uniqueId} from '@/library/string';
import TopMessageHandler from '@/library/topMessageHandler';
import store from '@/store';
import {errorHandler, XHR_STATUS_ERROR, XHR_STATUS_INVALID} from '@/utils/portuna';
import axios from 'axios';
import {reactive} from 'vue';

export function buildSelect(categories: ICategory[], depth = 0, categoryList: SelectOption[] = []): SelectOption[]
{
    for (let category of categories)
    {
        categoryList.push({
            value: category.id,
            label: (depth > 0 ? '  '.repeat(depth) + ' ∟' : '') + category.name,
            attributes: {},
            badgeColor: `#${category.color}`,
        });

        // children
        if (category.children && category.children.length)
        {
            buildSelect(category.children, depth + 1, categoryList);
        }
    }

    return categoryList;
}

export class CategoryManager implements ICategoryManager
{
    route: string = '';

    allowCategoryParents: boolean = false;

    allowModuleAdmin: boolean = false;

    allowEntryDelete: boolean = false;

    useColor: boolean = true;

    edit: number = 0;

    deleteMoveSelect: number[] = reactive([]);

    categories: Record<string, ICategory> = reactive({});

    changeParent: {
        categories: number[]
        parent: number
    }[] = reactive([]);

    categoriesSelect: ICategory[] = reactive([]);

    _uid = uniqueId();

    constructor(route?: string)
    {
        if (route)
        {
            this.route = route;
        }
    }

    hierarchy(): Hierarchy<ICategory>
    {
        return this.createHierarchy(this.categories);
    }

    categoriesSorted(): ICategory[]
    {
        return this.sortHierarchy(this.hierarchy(), true);
    }

    categoriesAssoc(): Record<number, ICategory>
    {
        let categoriesAssoc: Record<number, ICategory> = {};

        for (const category of this.categoriesSelect)
        {
            categoriesAssoc[category.id] = category;
        }

        return categoriesAssoc;
    }

    categoryColorIndex(): Record<string, number>
    {
        let categoryColorIndex: Record<string, number> = {};
        let index: number                              = 0;

        const generateIndex = (categories: ICategory[]) =>
        {
            for (let category of categories)
            {
                categoryColorIndex[category.id] = index++;

                if (Array.isArray(category.children))
                {
                    generateIndex(category.children);
                }
            }
        };

        generateIndex(this.categoriesSorted());

        return categoryColorIndex;
    }

    entryCategoriesSelect(): SelectOption[]
    {
        return buildSelect(this.sortHierarchy(this.createHierarchy(this.categoriesSelect), true));
    }

    categoryList(): ICategory[]
    {
        let categories: ICategory[] = this.categoriesSorted().slice();

        for (let i = 0; i < categories.length; i++)
        {
            if (categories[i].id === 0)
            {
                categories.splice(i, 1);
                break;
            }
        }

        return categories;
    }

    categoryCreateSelect(): SelectOption[]
    {
        return buildSelect(this.categoriesSorted());
    }

    categoryDeleteSelect(): SelectOption[]
    {
        let categories: ICategory[] = [];

        for (let categoryId of this.deleteMoveSelect)
        {
            categories.push(this.categories[categoryId]);
        }

        return buildSelect(this.sortHierarchy(this.createHierarchy(categories)));
    }

    createHierarchy(categories: ICategory[] | { [index: string]: ICategory }): Hierarchy<ICategory>
    {
        if ('undefined' === typeof categories)
        {
            categories = [];
        }
        else if (!Array.isArray(categories))
        {
            categories = Object.values(categories);
        }

        return new Hierarchy(categories, 'id', 'parent');
    }

    sortHierarchy(hierarchy: Hierarchy<ICategory>, entrySelect = false)
    {
        let entries = hierarchy.sortArray((categoryA, categoryB) =>
        {
            if (categoryA.id === 0)
            {
                return -1;
            }
            if (categoryB.id === 0)
            {
                return 1;
            }

            return categoryA.name.localeCompare(categoryB.name);
        });

        if (!entrySelect)
        {
            for (let entry of entries)
            {
                if (entry.id === 0)
                {
                    entry.name = 'Keine übergeordnete Kategorie';
                    break;
                }
            }
        }

        return entries;
    }

    categoryUpdate(data: ICategory): void
    {
        this.categories[data.id] = data;

        let updated = false;

        for (let i in this.categoriesSelect)
        {
            if (this.categoriesSelect.hasOwnProperty(i) && this.categoriesSelect[i].id === data.id)
            {
                this.categoriesSelect[i] = data;
                updated                  = true;

                break;
            }
        }

        if (!updated)
        {
            this.categoriesSelect.push(data);

            this.categoriesSelect.sort((a, b) =>
            {
                let nameA = a.name.toLowerCase(),
                    nameB = b.name.toLowerCase();

                return nameA > nameB ? 1 : (nameA === nameB ? 0 : -1);
            });
        }
    }

    setSelectCategories(categories: ICategory[]): void
    {
        this.categoriesSelect = categories;
    }

    changeParentReset(): void
    {
        this.changeParent = [];
    }

    update(data: ICategoryManager): void
    {
        let updateData: ICategoryManager = {
            allowCategoryParents: data.allowCategoryParents,
            allowModuleAdmin: data.allowModuleAdmin,
            allowEntryDelete: data.allowEntryDelete,
            useColor: data.useColor,
            categories: data.categories,
        };

        if (Array.isArray(updateData.categories))
        {
            let categories: Record<string, ICategory> = {};

            for (let category of updateData.categories)
            {
                categories[category.id] = category;
            }

            updateData.categories = categories;
        }

        for (const key in updateData)
        {
            if (updateData.hasOwnProperty(key) && this.hasOwnProperty(key))
            {
                this[key] = updateData[key];
            }
        }
    }

    delete(categoryIds: number[])
    {
        for (let categoryId of categoryIds)
        {
            delete this.categories[categoryId];
        }

        for (let i in this.categoriesSelect)
        {
            if (this.categoriesSelect.hasOwnProperty(i) && categoryIds.indexOf(this.categoriesSelect[i].id) !== -1)
            {
                this.categoriesSelect.splice(parseInt(i), 1);
            }
        }
    }

    set(data: ICategoryManager): void
    {
        // im guessing this is just an alias
        this.update(data);
    }

    saveParents()
    {
        // skip save if no changes available
        if (this.changeParent.length === 0)
        {
            return;
        }

        Loader.show('Die Sortierung wird gespeichert...');
        axios.post(this.route + '/save-parent-change', {update: this.changeParent})
        .then(() =>
        {
            for (const data of this.changeParent)
            {
                const parent = data.parent;

                for (let categoryId of data.categories)
                {
                    this.categoryUpdate(Object.assign({}, this.categories[categoryId], {parent}));
                }
            }

            this.changeParentReset();

            TopMessageHandler.success('Die Änderungen wurden gespeichert.');
        })
        .catch(errorHandler)
        .finally(Loader.hide);
    }

    /**
     * save (add/edit) a category
     */
    save(data: ICategory): Promise<void>
    {
        Loader.show(data.id === 0 ? 'Die Kategorie wird erstellt...' : 'Die Kategorie wird gespeichert...');

        return new Promise((resolve, reject) =>
        {
            store.dispatch('form/setErrors', {}).catch(console.error);
            axios.post(this.route + '/save-category', data).then(({data}) =>
            {
                if (XHR_STATUS_ERROR === data.status)
                {
                    reject(data);
                    return;
                }
                else if (XHR_STATUS_INVALID === data.status)
                {
                    store.dispatch('form/setErrors', data.data.errors).catch(console.error);
                    reject(data);
                    return;
                }

                resolve();

                this.categoryUpdate(data.data);

                if (data.id === 0)
                {
                    TopMessageHandler.success('Die Kategorie wurde erfolgreich gespeichert.');
                }
                else
                {
                    TopMessageHandler.success('Die Kategorie wurde erfolgreich bearbeitet');
                }
            })
            .catch(reject)
            .finally(Loader.hide);
        });
    }
}